import { workerPerformanceLink } from './workerPerformance'

type SetupItem = {
  label: string
  to: string
  hint: string
  keywords?: string
  kind?: 'Report'
}

type SetupCategory = {
  id: string
  label: string
  scene: string
  summary: string
  items: SetupItem[]
}

export const setupDirectory: SetupCategory[] = [
  {
    id: 'organization',
    label: 'Company & team',
    scene: 'people',
    summary: 'Company profile, user access and team activity',
    items: [
      {
        label: 'Company profile',
        to: '/setup/edit',
        hint: 'Change the company name, main phone number, logo or Form W-9.',
        keywords: 'admin organization details branding w9 tax form owner',
      },
      {
        label: 'Users & permissions',
        to: '/users',
        hint: 'Choose which parts of the CRM each team member can access.',
        keywords: 'admin staff employees roles permissions',
      },
      {
        label: 'Shared tags',
        to: '/setup/tags',
        hint: 'Manage the tags your team uses to organize records.',
        keywords: 'admin organization',
      },
      {
        ...workerPerformanceLink,
        kind: 'Report',
        hint: 'View daily and hourly activity by team member.',
        keywords: 'admin call stats reports',
      },
    ],
  },
  {
    id: 'leads',
    label: 'Leads & campaigns',
    scene: 'campaigns',
    summary: 'Lead stages, follow-ups and campaign contacts',
    items: [
      {
        label: 'Lead pipeline',
        to: '/setup/pipeline',
        hint: 'Set lead stages and choose which emails or texts a status triggers.',
        keywords: 'marketing sales statuses follow-up automation estimate',
      },
      {
        label: 'Campaign contact statuses',
        to: '/setup/statuses',
        hint: 'Choose the status options for contacts in outreach campaigns.',
        keywords: 'crm campaign statuses sms blasts',
      },
      {
        label: 'Contact trades',
        to: '/setup/trades',
        hint: 'Manage the trade categories you assign to campaign contacts.',
        keywords: 'crm professions contractors',
      },
    ],
  },
  {
    id: 'projects',
    label: 'Projects & scheduling',
    scene: 'house',
    summary: 'Job types, project records, crews and calendar labels',
    items: [
      {
        label: 'Job types',
        to: '/setup/job-types',
        hint: 'Define restoration work types and their default tasks.',
        keywords: 'manager milestones',
      },
      {
        label: 'Project tabs',
        to: '/setup/tabs',
        hint: 'Choose the sections available in a project record.',
        keywords: 'manager layout',
      },
      {
        label: 'Insurance companies',
        to: '/setup/insurance-companies',
        hint: 'Manage the list of insurance carriers available on projects.',
        keywords: 'manager insurers',
      },
      {
        label: 'Checklists',
        to: '/setup/checklists',
        hint: 'Checklist templates for jobs: sections, tasks, photos and questions. Import from CompanyCam.',
        keywords: 'companycam checklist template tasks inspection',
      },
      {
        label: 'Crews',
        to: '/setup/crews',
        hint: 'Manage field teams, their time off and their Job Photos app access (PIN and departments).',
        keywords: 'dispatch scheduling availability',
      },
      {
        label: 'Saved places & GPS',
        to: '/setup/saved-places',
        hint: 'Set what counts as a stop on a crew route, and save places crews stop at (yard, suppliers, dump).',
        keywords:
          'gps location route tracking time clock stops supplier yard map',
      },
      {
        label: 'Project labels',
        to: '/setup/labels',
        hint: 'Set the names and colors used to label scheduled projects.',
        keywords: 'calendar scheduling',
      },
    ],
  },
  {
    id: 'billing',
    label: 'Billing & payments',
    scene: 'finance',
    summary: 'Bill types, money received, payouts and QuickBooks',
    items: [
      {
        label: 'Bill types',
        to: '/setup/bill-types',
        hint: 'Set up bill categories and the tasks associated with each type.',
        keywords: 'manager invoice',
      },
      {
        label: 'Incoming payment methods',
        to: '/setup/payment-methods',
        hint: 'Choose the methods available when recording money received.',
        keywords: 'manager customer payments paid in',
      },
      {
        label: 'Outgoing payment methods',
        to: '/setup/payout-methods',
        hint: 'Choose the methods available when recording money paid out.',
        keywords: 'manager payout methods payouts expenses',
      },
      {
        label: 'QuickBooks',
        to: '/setup/quickbooks',
        hint: 'Connect your accounting system and choose how payments sync.',
        keywords: 'manager integration payment review qb quick books',
      },
    ],
  },
  {
    id: 'communications',
    label: 'Calls & messages',
    scene: 'messages',
    summary:
      'Phone lines, call routing, inquiry tracking and message templates',
    items: [
      {
        label: 'CRM phone numbers',
        to: '/setup/phone-numbers',
        hint: 'Set up calling and texting, add phone lines or sync existing numbers.',
        keywords: 'admin twilio dialer sms activate service',
      },
      {
        label: 'Call routing',
        to: '/setup/flows',
        hint: 'Choose where incoming calls go and when they reach voicemail.',
        keywords: 'admin call flows routing assigned numbers',
      },
      {
        label: 'Inquiry call tracking',
        to: '/leads/tracking/settings',
        hint: 'Connect RingCentral, match numbers to marketing sources and set up the Workiz import.',
        keywords: 'inquiry settings leads extensions blocked numbers reporting',
      },
      {
        label: 'Message templates',
        to: '/setup/templates',
        hint: 'Write reusable email and SMS messages for your team.',
        keywords: 'manager text messaging',
      },
    ],
  },
  {
    id: 'ai',
    label: 'AI tools',
    scene: 'settings',
    summary: 'Writing instructions, bill comparison and matching terms',
    items: [
      {
        label: 'AI prompts',
        to: '/setup/prompts',
        hint: 'Edit the instructions for drafting emails, rewriting text and comparing bills.',
        keywords: 'admin artificial intelligence',
      },
      {
        label: 'AI synonyms',
        to: '/setup/synonyms',
        hint: 'Group words and abbreviations that the AI should treat as the same term.',
        keywords: 'admin artificial intelligence aliases matching',
      },
    ],
  },
]

export function searchSetup(query: string) {
  const words = (value: string) =>
    value.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []
  const terms = words(query)
  if (terms.length === 0) return []
  return setupDirectory
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const index = words(
          [group.label, item.label, item.hint, item.keywords, item.kind].join(
            ' '
          )
        )
        return terms.every((term) =>
          index.some((word) => word.startsWith(term))
        )
      }),
    }))
    .filter((group) => group.items.length > 0)
}

export function setupLocation(path: string) {
  for (const group of setupDirectory) {
    const item = group.items.find(
      ({ to }) => path === to || path.startsWith(to + '/')
    )
    if (item) return { group, item }
  }
}
